(function () {
  const HANDLE = 'navidium-shipping-protection';
  const OPTOUT_KEY = 'nvd-optout';
  // Market-aware root path ('/' for AUD, '/en-nz/' for NZ) — prefixed on all storefront fetches
  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const DEBOUNCE_MS = 50;
  const MUTEX_RESET_MS = 100;
  // 4000ms measured too short in practice: a cold page load's init() (fetchCart + loadVariants +
  // an initial syncProtection swap) alone can take ~2-4s on this store's real network latency,
  // leaving the verify-and-fix loop no time to actually check/fix anything before forceCleanProtection
  // fired and stripped the item. Raised so the loop can wait out a full swap, not just a page load.
  const CHECKOUT_WAIT_MS = 9000;
  const CLEANUP_TIMEOUT_MS = 1500;
  // The checkout guard won't trust a "consistent" cart until the protectable base is unchanged across
  // two reads STABILITY_MS apart. Shopify serves /cart.js (and the server-rendered /cart) from lagging
  // replicas, so right after a native add/remove a single read can return a stale-but-internally-
  // consistent snapshot (old item set + matching SP) that the guard would otherwise settle on and ship
  // a stale-tier SP. Requiring two agreeing reads waits out that replica lag before submitting.
  const STABILITY_MS = 400;
  const DESC_ON = "Covers your order if it's damaged, stolen, or lost during transit.";
  const DESC_OFF = 'Without protection, damaged, stolen or lost deliveries may not be covered.';
  const DISCLAIMER = 'This is an optional additional service for expedited replacements and is not a shipping fee.';

  // ===== Shipping-protection rate per market currency — EDIT THESE TO CHANGE THE % =====
  // Key = the cart's currency code (from /cart.js), value = fraction (0.02 = 2%, 0.03 = 3%).
  // Add more markets as needed, e.g. USD: 0.025. Currencies not listed fall back to DEFAULT.
  const PROTECTION_PCT = { AUD: 0.02, NZD: 0.02 };
  const PROTECTION_PCT_DEFAULT = 0.02;
  // Cap on the protection charge, in AUD cents (the tier ladder is AUD; other markets inherit it via
  // presentment conversion). Orders above cap ÷ % pay this flat max instead of the full percentage —
  // e.g. 1950 (= $19.50) at 2% caps at a $975 order, so large/bulk orders aren't dissuaded by a high
  // SP fee. Must match an existing variant on the ladder (1950 = NVDPROTECTION26). EDIT TO CHANGE CAP.
  const MAX_PROTECTION_CENTS = 1950;
  // =====================================================================================

  let variants = [];
  let isUpdating = false;
  let lastCart = null;
  // False from page load until the initial fetch + stray-duplicate cleanup has completed. Guards
  // against a duplicate protection item left over from a PREVIOUS page's swap being interrupted by
  // navigation (add-to-cart triggering a page load, or the checkout click itself) — the checkout
  // guard below blocks on this in addition to isUpdating, so there's no gap between page load and
  // the point we've confirmed/cleaned the cart's protection state.
  let ready = false;
  // True synchronously from the instant a cart:updated event is received until the (debounced)
  // handler actually starts processing it. Without this, a checkout click landing inside the
  // DEBOUNCE_MS window — after an add/remove has already mutated the server-side cart but before
  // syncProtection has run (and set isUpdating) — would see isUpdating === false and slip through
  // with a stale protection state (e.g. zero protection items right after the first add crossed a
  // tier). guardCheckout blocks on this the same way it blocks on isUpdating.
  let pending = false;
  // Count of cart-MUTATING network requests (/cart/add|change|update|clear) currently in flight —
  // ours AND, critically, the theme's own (quantity stepper, "Remove" link, add-to-cart form). The
  // theme mutates the server cart asynchronously and only dispatches cart:updated AFTER its round
  // trip returns, so between the click and that event ALL of our flags (ready/isUpdating/pending)
  // read false while the server cart is mid-change. A checkout click landing in that window would
  // otherwise have guardCheckout's verify-fetch RACE the theme's write: the fetch can win, observe
  // the pre-change cart, judge protection "consistent" for the old quantity, and submit a stale/
  // wrong-tier protection item (reproduced: bump qty 1→2 then checkout fast → shipped tier 3.75 for
  // a 2-item cart that should be 7.5). Wrapping fetch/XHR lets the guard wait out any such write;
  // once it resolves, the guard's own re-verify sees the true cart and fixes the tier before submit.
  let cartWritesInFlight = 0;
  // True when a cart change arrived while a sync was already running, so that sync must re-verify
  // against a fresh cart before it's allowed to finish. See syncProtection's pass loop.
  let resyncNeeded = false;
  // Hard ceiling on the re-verify loop so a pathological flip-flopping cart can't spin forever.
  const MAX_SYNC_PASSES = 6;
  // Longest we'll wait for a THEME cart write (stepper/Remove) to land before re-verifying.
  const WRITE_DRAIN_MS = 3000;
  // Bounded re-read budget for outrunning Shopify's read-replica lag (6 x 150ms = 900ms worst case).
  const DOM_SETTLE_TRIES = 6;
  const DOM_SETTLE_MS = 150;
  // The un-wrapped window.fetch, captured before trackCartWrites() patches it. All of OUR OWN cart
  // reads and writes go through this, so cartWritesInFlight counts ONLY writes we don't control
  // (the theme's quantity stepper, "Remove" link, add-to-cart form). Without this, syncProtection's
  // own add/remove would inflate the counter and make the re-verify loop below think a foreign
  // mutation was outstanding after every single pass.
  let rawFetch = window.fetch ? window.fetch.bind(window) : null;
  (function trackCartWrites() {
    const isWrite = (url, method) => {
      try {
        return String(method || 'GET').toUpperCase() !== 'GET' &&
          /\/cart\/(add|change|update|clear)(\.js)?/.test(String(url));
      } catch (e) { return false; }
    };
    // Release on a macrotask, not synchronously on settle, so the theme's ensuing cart:updated (and
    // our pending flag / syncProtection) has a beat to take over the gate — no flicker to 0 that a
    // waiting checkout loop could slip through between the write finishing and the event firing.
    const release = () => setTimeout(() => { if (cartWritesInFlight > 0) cartWritesInFlight--; }, 0);
    const origFetch = window.fetch;
    if (typeof origFetch === 'function') {
      window.fetch = function (input, init) {
        const url = (input && typeof input === 'object' && 'url' in input) ? input.url : input;
        const method = (init && init.method) || (input && input.method);
        if (!isWrite(url, method)) return origFetch.apply(this, arguments);
        cartWritesInFlight++;
        return origFetch.apply(this, arguments).then(
          (r) => { release(); return r; },
          (e) => { release(); throw e; }
        );
      };
    }
    const XHR = window.XMLHttpRequest;
    if (XHR && XHR.prototype) {
      const open = XHR.prototype.open, send = XHR.prototype.send;
      XHR.prototype.open = function (method, url) { this.__spCartWrite = isWrite(url, method); return open.apply(this, arguments); };
      XHR.prototype.send = function () {
        if (this.__spCartWrite) {
          cartWritesInFlight++;
          let released = false;
          const rel = () => { if (released) return; released = true; release(); };
          this.addEventListener('loadend', rel);
        }
        return send.apply(this, arguments);
      };
    }
  })();

  function debounce(fn, ms) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  }
  function formatPrice(cents) { return '$' + (cents / 100).toFixed(2); }
  function nonNvdItems(cart) { return cart.items.filter(i => i.handle !== HANDLE); }
  function nvdItemsOf(cart) { return cart.items.filter(i => i.handle === HANDLE); }
  // Sum of cart value eligible for protection (excludes the NVD item and gift cards by SKU)
  function protectableBaseCents(items) {
    return items
      .filter(i => i.sku !== 'gift-card' && i.product_type !== 'Gift Cards' && i.handle !== HANDLE)
      .reduce((sum, i) => sum + i.line_price, 0);
  }
  function pctFor(currency) {
    return Object.prototype.hasOwnProperty.call(PROTECTION_PCT, currency) ? PROTECTION_PCT[currency] : PROTECTION_PCT_DEFAULT;
  }
  function calculateVariant(items, currency) {
    const baseCents = protectableBaseCents(items);
    if (baseCents === 0) return null;
    // Protection % is per-currency (PROTECTION_PCT). Compare that % of the cart total (capped at $50)
    // against the canonical AUD ladder (`v.aud`, from the static variant titles); Shopify then converts
    // the chosen variant to the market currency. (Selecting against `v.aud` not `v.price` avoids the
    // per-variant-rounded presentment prices.)
    const target = Math.min(baseCents * pctFor(currency), MAX_PROTECTION_CENTS);
    return variants.find(v => v.aud >= target) || variants[variants.length - 1];
  }
  // Hide the whole widget card when nothing in the cart is protectable (e.g. gift-card-only cart)
  function setCardVisible(show) {
    const card = document.querySelector('.cart-navidium-card');
    if (card) card.style.display = show ? '' : 'none';
  }
  function updateWidgetPrice(cart) {
    setCardVisible(protectableBaseCents(cart.items) > 0);
    const el = document.querySelector('#nvd-custom-price');
    if (!el) return;
    // Prefer the actual protection line price (already in the market currency) for an exact display
    const nvd = nvdItemsOf(cart)[0];
    if (nvd) { el.textContent = formatPrice(nvd.price); return; }
    const v = calculateVariant(cart.items, cart.currency);
    if (v) el.textContent = formatPrice(v.price);
  }
  function renderWidget(cart) {
    const container = document.querySelector('.nvd-mini');
    if (!container) return;
    let toggle = container.querySelector('#nvd-custom-toggle');
    if (!toggle) {
      // Fallback build (skeleton is normally server-rendered in section.main-cart.liquid)
      const icon = `<svg class="nvd-shield-svg" width="36" height="48" viewBox="4 2 38 51" fill="none" aria-hidden="true"><path class="nvd-shield-body" d="M20 3 L7 6 Q4 6.5 4 10 L4 22 C4 33 11 42 20 47 C29 42 36 33 36 22 L36 10 Q36 6.5 33 6 L20 3 Z" fill="#30343b"/><path d="M20 3 L7 6 Q4 6.5 4 10 L4 22 C4 33 11 42 20 47 C29 42 36 33 36 22 L36 10 Q36 6.5 33 6 L20 3 Z" transform="translate(4 5) scale(0.8)" fill="none" stroke="#ffffff" stroke-width="2"/><g transform="translate(18.4 21.8) scale(0.6)"><path class="nvd-badge" d="M20 3 L7 6 Q4 6.5 4 10 L4 22 C4 33 11 42 20 47 C29 42 36 33 36 22 L36 10 Q36 6.5 33 6 L20 3 Z" fill="#2fae4f" stroke="#ffffff" stroke-width="6" paint-order="stroke" stroke-linejoin="round"/><path d="M11 27 L17 33 L29 18" fill="none" stroke="#ffffff" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round"/></g></svg>`;
      container.innerHTML = `<label class="nvd-custom-widget">
        <input type="checkbox" id="nvd-custom-toggle" class="nvd-sr-only" role="switch" checked>
        <span class="nvd-icon">${icon}</span>
        <span class="nvd-custom-content">
          <strong class="nvd-title">Priority Shipping Protection</strong>
          <span class="nvd-sub">from Damage, Loss &amp; Theft for <span id="nvd-custom-price"></span></span>
          <span class="nvd-desc" id="nvd-custom-desc">${DESC_ON}</span>
        </span>
        <span class="nvd-toggle" aria-hidden="true"><span class="nvd-track"><span class="nvd-thumb"></span></span></span>
      </label>`;
      toggle = container.querySelector('#nvd-custom-toggle');
    }
    // Hydrate: sync checked state + description from sessionStorage, fill price, bind once
    const optedOut = sessionStorage.getItem(OPTOUT_KEY);
    toggle.checked = !optedOut;
    const descEl = container.querySelector('#nvd-custom-desc');
    if (descEl) descEl.textContent = optedOut ? DESC_OFF : DESC_ON;
    updateWidgetPrice(cart);
    if (!toggle.dataset.bound) {
      toggle.addEventListener('change', onToggle);
      toggle.dataset.bound = '1';
    }
  }
  // cache: 'no-store' on both cart reads: after a NATIVE add-to-cart (product form POSTs /cart/add
  // and the browser follows Shopify's 302 to /cart — see block.product-buy-buttons.js's "let the form
  // submit normally" path), the new line item is committed server-side, but /cart.js carries no
  // Cache-Control header, so a browser MAY serve init()'s (and the guard's) fetch from heuristic HTTP
  // cache — a stale cart missing the just-added item. calculateVariant would then judge the OLD tier
  // correct, skip the swap, flip ready=true, and the guard would ship the stale protection item even
  // though the cart list on screen shows the new item. no-store forces the live cart every time.
  function spFetch() { return (rawFetch || window.fetch).apply(null, arguments); }
  function fetchCart() { return spFetch(ROOT + 'cart.js', { cache: 'no-store' }).then(r => r.json()); }
  function fetchSection() { return spFetch(ROOT + '?section_id=cart-ajax', { cache: 'no-store' }).then(r => r.text()); }
  // Quantities the customer can currently SEE, keyed by line key. The stepper writes the input value
  // synchronously on click (element.quantity-selector.js) — before its network write even starts — so
  // this is the most current statement of intent available, and the right thing to reconcile against.
  function domQuantities() {
    const rows = document.querySelectorAll('.cart__item[data-key]');
    if (!rows.length) return null;
    const map = new Map();
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const input = row.querySelector('.element-quantity-selector__input');
      const q = input ? parseInt(input.value, 10) : NaN;
      if (row.dataset.key && !isNaN(q)) map.set(row.dataset.key, q);
    }
    return map.size ? map : null;
  }
  // Has this cart read caught up with the page? Shopify serves /cart.js AND the rendered section from
  // LAGGING REPLICAS, so a read taken just after a write can come back internally consistent but a
  // whole change behind. Settling on one of those is what left protection on the previous quantity's
  // tier while the line items on screen already showed the new one. Returns true when there's nothing
  // to compare (no cart page rendered), so this can never block a non-cart page.
  function cartAgreesWithDom(cart) {
    const dom = domQuantities();
    if (!dom || !cart || !Array.isArray(cart.items)) return true;
    const real = nonNvdItems(cart);
    if (real.length !== dom.size) return false;
    for (let i = 0; i < real.length; i++) {
      if (dom.get(real[i].key) !== real[i].quantity) return false;
    }
    return true;
  }
  // Read the cart, re-reading until it agrees with the page (or the budget runs out — never block
  // forever; a disagreement we can't resolve just falls through to the freshest read we managed).
  async function fetchCartSettled() {
    let cart = await fetchCart();
    for (let i = 0; i < DOM_SETTLE_TRIES && !cartAgreesWithDom(cart); i++) {
      await new Promise((r) => setTimeout(r, DOM_SETTLE_MS));
      await waitForCartWrites();
      cart = await fetchCart();
    }
    return cart;
  }
  // The section render lags independently of /cart.js, so a section fetched even after a settled cart
  // read can still paint a pre-change subtotal over the top of the theme's correct one — exactly the
  // reported "line items show qty 2, subtotal shows qty 1". Re-fetch until the rendered markup carries
  // the total we expect; return null if it never does, so the caller skips the paint rather than
  // clobbering the theme's render with stale markup.
  async function fetchSectionFor(cart) {
    const want = cart && typeof cart.total_price === 'number' ? formatPrice(cart.total_price) : null;
    let html = await fetchSection();
    if (!want) return html;
    for (let i = 0; i < DOM_SETTLE_TRIES && html.indexOf(want) === -1; i++) {
      await new Promise((r) => setTimeout(r, DOM_SETTLE_MS));
      html = await fetchSection();
    }
    return html.indexOf(want) === -1 ? null : html;
  }
  // Wait for any THEME cart write to finish. Returns true if we actually had to wait — the caller
  // treats that as "the cart moved under me, re-verify from a fresh read".
  async function waitForCartWrites() {
    if (cartWritesInFlight <= 0) return false;
    const deadline = Date.now() + WRITE_DRAIN_MS;
    while (cartWritesInFlight > 0 && Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 20));
    }
    return true;
  }
  function applySubtotal(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    const newSub = div.querySelector('.cart__subtotal');
    const dataSubtotal = document.querySelector('[data-subtotal]');
    if (newSub && dataSubtotal) dataSubtotal.innerHTML = newSub.outerHTML;
  }
  function cartRemove(lineKey) {
    // /cart/change.js returns the full cart AND (via `sections`) the re-rendered cart section in the
    // same response — saves a separate fetchCart/fetchSection round-trip. A 400 (e.g. removing a line
    // key that's already gone, which can happen when two passes race) still returns a JSON body, just
    // an error one (no `.items`) — return null instead of that error object so callers never mistake
    // it for a real cart and poison lastCart/updatedCart with it.
    return spFetch(ROOT + 'cart/change.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0, sections: 'cart-ajax', sections_url: ROOT + 'cart' }),
    }).then(r => (r.ok ? r.json() : null)).catch(() => null);
  }
  function sectionFrom(cart) { return cart && cart.sections && cart.sections['cart-ajax']; }
  function cartAdd(variantId) {
    return spFetch(ROOT + 'cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, properties: { 'Disclaimer': DISCLAIMER } }),
    });
  }
  function dispatchCartUpdated(cart, sectionHtml) {
    // Wrapped defensively: a third-party cart:updated/cart:build listener throwing must never be able
    // to abort the caller (syncProtection's finally, init's ready = true) even though the DOM spec
    // already isolates listener exceptions from dispatchEvent — this removes any ambiguity.
    try {
      if (sectionHtml) {
        applySubtotal(sectionHtml);
      } else {
        document.dispatchEvent(new CustomEvent('cart:build'));
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    } catch (err) { console.error('[shipping-protection] cart:updated listener threw', err); }
  }
  // One verify-and-fix pass against the given cart snapshot. Returns { updatedCart, sectionHtml } —
  // deliberately does NOT touch the DOM or dispatch events, so the caller can discard the result of a
  // pass whose snapshot was overtaken by a newer cart change (painting stale section HTML is what
  // left the subtotal showing the previous quantity).
  async function syncProtectionOnce(cart) {
    {
      const optedOut = sessionStorage.getItem(OPTOUT_KEY);
      const nvdItems = cart.items.filter(i => i.handle === HANDLE);
      const current = nvdItems[0] || null;
      // "Excess" = more than exactly ONE protection unit. Covers BOTH multiple line items AND a single
      // line with quantity > 1 — Shopify merges a re-added identical variant into one line with qty 2
      // (reproduced: a replica-lagged read shows no SP, the add-only branch re-adds the same variant →
      // qty 2 → charged twice). Counting only nvdItems.length would miss the qty>1 case entirely.
      const totalNvdQty = nvdItems.reduce((sum, i) => sum + i.quantity, 0);
      const hasExcess = nvdItems.length > 1 || totalNvdQty > 1;
      const target = calculateVariant(cart.items, cart.currency);
      let updatedCart = null;
      let sectionHtml = null;
      if (optedOut || !target) {
        // Remove all NVD items in parallel (handles duplicates too); take whichever response carries
        // a usable cart (a 400 on one racing removal now returns null instead of an error object).
        const removed = await Promise.all(nvdItems.map((item) => cartRemove(item.key)));
        updatedCart = removed.reverse().find(Boolean) || null;
        if (updatedCart) sectionHtml = sectionFrom(updatedCart);
      } else if (hasExcess) {
        // Too many protection units (extra line items and/or qty>1): remove every SP line in parallel,
        // then re-add exactly one of the correct variant. Normalizes any over-count to a single unit.
        await Promise.all(nvdItems.map((item) => cartRemove(item.key)));
        await cartAdd(target.id);
        [updatedCart, sectionHtml] = await Promise.all([fetchCart(), fetchSection()]);
      } else if (!current) {
        // Add-only: add then fetch cart + section in parallel (add.js can't return the full cart)
        await cartAdd(target.id);
        [updatedCart, sectionHtml] = await Promise.all([fetchCart(), fetchSection()]);
      } else if (current.variant_id !== target.id) {
        // Swap: remove old, THEN add new — deliberately in this order, not add-then-remove. If page
        // navigation (e.g. an add-to-cart that triggers a full page load, or the checkout click
        // itself) interrupts the JS mid-swap, this ordering means the cart is briefly missing
        // protection (safe: no charge) rather than briefly holding two protection line items (unsafe:
        // possible double charge at checkout). See guardCheckout()/ready for the complementary fix
        // that closes the page-load registration gap this same failure mode could exploit.
        await cartRemove(current.key);
        await cartAdd(target.id);
        [updatedCart, sectionHtml] = await Promise.all([fetchCart(), fetchSection()]);
      }
      return { updatedCart, sectionHtml };
    }
  }
  // Serialized entry point. Earlier this returned silently when a sync was already running, which
  // DROPPED the cart change entirely: two fast quantity clicks (3→2, then 2→1 before the first swap
  // finished) left the second change unprocessed and shipped the qty-2 SP tier on a qty-1 cart.
  // Now a concurrent change sets resyncNeeded, and the running sync loops again against a FRESH cart
  // read instead of exiting — so the last cart state always wins.
  async function syncProtection(cart) {
    if (!variants.length) return;
    if (isUpdating) { resyncNeeded = true; return; }
    isUpdating = true;
    // Any network failure below (offline, Shopify hiccup) must still clear isUpdating — otherwise
    // it gets stuck true forever and guardCheckout() would defer every future checkout click.
    try {
      let snapshot = cart;
      let latest = null;      // freshest cart we've seen — what we finally render from
      let latestHtml = null;
      let mutated = false;
      for (let pass = 0; pass < MAX_SYNC_PASSES; pass++) {
        // Take a fresh read before doing any work when the cart is known to have moved — either on a
        // later pass, or on the FIRST pass when resyncNeeded was already set before we were called.
        // That first-pass case is the init() path: a quantity click during page-load setup flags the
        // change while `ready` is still false, and `cart` (fetched at the top of init) predates it.
        // Without this the flag would just be cleared and the sync would settle on the stale tier.
        if (resyncNeeded || pass > 0 || !cartAgreesWithDom(snapshot)) {
          resyncNeeded = false;
          await waitForCartWrites();
          snapshot = await fetchCartSettled();
          latest = snapshot;
          latestHtml = null; // section HTML captured for the previous snapshot is stale — refetched below
        }
        resyncNeeded = false;
        const res = await syncProtectionOnce(snapshot);
        if (res && res.updatedCart) {
          mutated = true;
          latest = res.updatedCart;
          latestHtml = res.sectionHtml || null;
        }
        // Did the cart move under us during that pass? Either another cart:updated arrived
        // (resyncNeeded) or a theme write is still in flight (stepper/Remove mid-round-trip).
        const waited = await waitForCartWrites();
        if (!resyncNeeded && !waited) break;
      }
      // Only ever paint the freshest cart/section, once, at the end of the loop.
      if (latest) {
        // Our own add/remove above re-read the cart immediately after writing it, so `latest` can be
        // a lagged replica read even though every pass did the right thing. Reconcile against the
        // page before painting, or we publish a cart the customer has already moved past.
        if (!cartAgreesWithDom(latest)) {
          latest = await fetchCartSettled();
          latestHtml = null;
        }
        if (mutated) {
          // Fetched LAST, after the settled read, and verified to carry this cart's total — a section
          // that never catches up yields null and we skip the paint entirely rather than overwrite the
          // theme's (correct) subtotal with a stale render.
          try { latestHtml = await fetchSectionFor(latest); } catch (e) { latestHtml = null; }
        }
        lastCart = latest;
        updateWidgetPrice(latest);
        if (mutated) dispatchCartUpdated(latest, latestHtml);
      }
    } finally {
      setTimeout(() => {
        isUpdating = false;
        // A change that landed during the MUTEX_RESET_MS tail has no running loop to pick it up —
        // kick off a fresh sync rather than let it be dropped (the original bug, one window over).
        if (resyncNeeded) {
          resyncNeeded = false;
          fetchCartSettled().then((c) => { lastCart = c; return syncProtection(c); }).catch(() => {});
        }
      }, MUTEX_RESET_MS);
    }
  }
  async function onToggle(e) {
    const descEl = document.querySelector('#nvd-custom-desc');
    if (e.target.checked) {
      sessionStorage.removeItem(OPTOUT_KEY);
      if (descEl) descEl.textContent = DESC_ON;
    } else {
      sessionStorage.setItem(OPTOUT_KEY, '1');
      if (descEl) descEl.textContent = DESC_OFF;
      window.igEvents = window.igEvents || []; window.igEvents.push({ "event": "javascripteventCPWEF" });
    }
    // Use cached cart — avoids a round-trip before the actual operation
    const cart = lastCart || await fetchCart();
    await syncProtection(cart);
  }
  // If the protection item is the only thing left in the cart, remove it and show the empty cart.
  // Prevents a lingering NVD-only cart (and checking out with just protection).
  async function enforceEmptyCart(cart) {
    const nvd = nvdItemsOf(cart);
    if (nonNvdItems(cart).length === 0 && nvd.length > 0) {
      // Sidebar/form on screen (e.g. minus'd the last item to 0): reload IMMEDIATELY — the empty cart
      // renders at once because real_item_count excludes the protection item, matching the instant
      // Remove link. Don't wait to delete the NVD first; the reloaded page cleans it up below.
      if (document.querySelector('.cart__page')) {
        window.location.reload();
        return true;
      }
      // Page already renders empty (NVD-only landing / post-reload): remove the stray item(s) in
      // parallel + refresh header. A racing removal can return null (already gone) — fall back to a
      // fresh fetch rather than ever dispatch a null/error cart to listeners.
      const removedResults = await Promise.all(nvd.map((item) => cartRemove(item.key)));
      const removed = removedResults.reverse().find(Boolean) || await fetchCart();
      document.dispatchEvent(new CustomEvent('cart:build'));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: removed } }));
      return true;
    }
    return false;
  }
  // Last-resort cleanup: if the checkout wait hits its hard ceiling without syncProtection ever
  // clearing isUpdating (network failure mid-swap), strip any protection line items rather than
  // let a possible duplicate ride through to checkout. Protection is a paid add-on, so silently
  // dropping it here is the safe trade-off — never silently double-charge for it. Bounded by its
  // own timeout so a second failure here can't extend the hang.
  function forceCleanProtection() {
    const withTimeout = (p, ms) => Promise.race([
      p,
      new Promise((resolve) => setTimeout(() => resolve(null), ms)),
    ]);
    return withTimeout(fetchCart(), CLEANUP_TIMEOUT_MS)
      .then((cart) => {
        if (!cart) return;
        const nvd = nvdItemsOf(cart);
        if (!nvd.length) return;
        return withTimeout(
          Promise.all(nvd.map((item) => cartRemove(item.key))),
          CLEANUP_TIMEOUT_MS
        );
      })
      .catch(() => {});
  }
  // Always intercept the checkout button and run a verify-and-fix pass against a FRESH cart fetch
  // before ever letting the real submit through — never trust cached flags (ready/isUpdating/pending)
  // alone, since those only reflect mutations this script knows about. The actual add/remove action
  // (theme's own add-to-cart form, quantity stepper, "Remove" link, etc.) can mutate the server-side
  // cart before our listeners ever fire, so a click could otherwise sail through against a stale
  // `lastCart` carrying the wrong-tier protection item. Registered unconditionally and synchronously
  // at script start (see bottom of file) — not gated behind any async setup — so there is no
  // registration gap on a freshly loaded page.
  // True while a verify-and-fix pass from a previous checkout click is still running. A customer
  // double-clicking (or double-tapping, trying to get through faster) would otherwise spawn a second
  // independent proceed() loop racing the first one over the same cart — both fetching/fixing
  // concurrently and stepping on each other's changes. Extra clicks while one is in flight are just
  // swallowed; the original click's own pass is already going to submit once it settles.
  let checkoutInFlight = false;
  function guardCheckout() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest && e.target.closest('[name="checkout"], .cart__checkout, .additional-checkout-buttons');
      if (!btn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (checkoutInFlight) { showLoading(); return; }
      checkoutInFlight = true;
      const form = btn.closest('form');
      // Keep the checkout button's spinner on for the WHOLE verify wait. The cart sidebar re-renders
      // while we settle (init's cart:updated swaps the button element for fresh HTML without the
      // class, and a stored reference would go stale/detached), so re-assert btn--loading on whatever
      // .cart__checkout is currently in the DOM — otherwise the spinner flickers off mid-wait and the
      // customer thinks the click did nothing.
      function showLoading() {
        const cb = document.querySelector('.cart__checkout');
        if (cb) cb.classList.add('btn--loading');
      }
      showLoading();
      // Resolve the form/button LIVE at submit time, not from the captured click-time references:
      // the cart section can be re-rendered during the verify wait (our cart:build/cart:updated events
      // trigger theme/app listeners that swap the sidebar HTML — the same reason the spinner needs
      // re-asserting), which would leave the captured `form`/`btn` detached and make requestSubmit() a
      // silent no-op. If no live form is found, navigate straight to checkout so the customer is NEVER
      // left stuck on the cart. (form.submit() is deliberately NOT used as a fallback — it omits the
      // name="checkout" submitter, so it would just re-save the cart instead of proceeding.)
      const CHECKOUT_URL = ROOT + 'checkout';
      const submit = () => {
        try {
          const liveBtn = document.querySelector('.cart__checkout, [name="checkout"]');
          const liveForm = (liveBtn && liveBtn.closest && liveBtn.closest('form')) ||
            (form && form.isConnected && form) ||
            document.querySelector('form[action*="/cart"]');
          if (liveForm && typeof liveForm.requestSubmit === 'function') {
            const submitter = (liveBtn && liveBtn.tagName === 'BUTTON' && liveForm.contains(liveBtn)) ? liveBtn : undefined;
            liveForm.requestSubmit(submitter);
            return;
          }
        } catch (err) { /* fall through to the guaranteed navigation below */ }
        window.location.href = CHECKOUT_URL;
      };
      const start = Date.now();
      const timeLeft = () => CHECKOUT_WAIT_MS - (Date.now() - start);
      const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
      function isMismatched(cart) {
        const nvdItems = nvdItemsOf(cart);
        const current = nvdItems[0] || null;
        // Excess protection: multiple lines OR a single line with qty>1 (Shopify merges a re-added
        // identical variant into one line at quantity 2). Counting only line-item count would let a
        // double-charged qty-2 line settle as "consistent" and ride through to checkout.
        const totalNvdQty = nvdItems.reduce((sum, i) => sum + i.quantity, 0);
        if (nvdItems.length > 1 || totalNvdQty > 1) return true;
        const optedOut = sessionStorage.getItem(OPTOUT_KEY);
        if (optedOut) return !!current;
        const target = calculateVariant(cart.items, cart.currency);
        if (!target) return !!current;
        return !current || current.variant_id !== target.id;
      }
      // Poll-fetch-verify-fix loop, bounded by CHECKOUT_WAIT_MS overall. A single fix-up call isn't
      // enough on its own: syncProtection() silently no-ops if another sync is already in flight
      // (isUpdating true), so a call made right as a concurrent swap starts would return immediately
      // without actually waiting for that swap to finish — the loop re-verifies against a fresh cart
      // each pass instead of trusting one attempt, so it naturally waits out any in-flight swap too.
      async function proceed() {
        let settled = false;
        let pass = 0;
        // Signature (non-SP item count + protectable base) of the previous consistent read. We only
        // settle when the current consistent read MATCHES it — i.e. the real cart held steady across
        // STABILITY_MS — so a replica-lagged snapshot can't be mistaken for the settled cart.
        let prevSig = null;
        while (timeLeft() > 0) {
          pass++;
          showLoading(); // keep the spinner on through re-renders for the whole wait
          if (!ready || isUpdating || pending || resyncNeeded || cartWritesInFlight > 0) {
            prevSig = null; // a mutation is in flight — any earlier "stable" read is void
            await sleep(20); continue;
          }
          let cart;
          try { cart = await fetchCart(); } catch (err) { break; }
          lastCart = cart;
          if (!variants.length) { settled = true; break; }
          if (!ready || isUpdating || pending || resyncNeeded || cartWritesInFlight > 0) { prevSig = null; continue; }
          // Stale-read guard, ONE direction only: if /cart.js reports MORE real line items than the
          // page is actually displaying, that read is replica-stale — still carrying an item the
          // customer already removed, whose OLD higher tier would look "consistent" for the stale base
          // and ride through (reproduced: remove item + fast checkout). Wait for the read to catch up.
          // Deliberately only the "fetched > displayed" case: a page showing >= as many rows as the
          // fetch is NEVER a reason to block (covers a lagging DOM, or SP ever appearing as a row), so
          // this can never strip SP or stall a healthy checkout. Bounded by CHECKOUT_WAIT_MS regardless.
          const domLineItems = document.querySelectorAll('.cart__item[data-key]').length;
          if (domLineItems > 0 && nonNvdItems(cart).length > domLineItems) {
            prevSig = null; await sleep(STABILITY_MS); continue;
          }
          const mism = isMismatched(cart);
          // Stability signature excludes the SP item (uses non-SP count + protectable base), so our own
          // SP swaps don't perturb it — only genuine cart changes / replica flip-flops do.
          const sig = nonNvdItems(cart).length + ':' + protectableBaseCents(cart.items);
          const stable = (sig === prevSig);
          if (mism) {
            // Wrong/duplicate/missing SP for THIS read — fix it, then re-confirm from scratch.
            await syncProtection(cart);
            prevSig = null;
            continue;
          }
          // SP matches this read. Only submit if the cart base also held steady across STABILITY_MS —
          // otherwise this may be a replica-stale snapshot; remember it and re-read to confirm.
          if (!stable) { prevSig = sig; await sleep(STABILITY_MS); continue; }
          settled = true; break;
        }
        if (!settled) {
          // Ran out of time without confirming a correct, single protection item — strip any stray
          // ones rather than let a stale/duplicate item ride through to checkout.
          await forceCleanProtection();
          try { lastCart = await fetchCart(); } catch { /* fall through with whatever lastCart has */ }
        }
        checkoutInFlight = false;
        const cart = lastCart;
        // Breadcrumb kept intentionally: the final SP variant vs. what the cart says it should be, the
        // last line before the page navigates. submitVariant !== submitTarget with settled:true would
        // be a stale item riding through — cheap early-warning if the guard ever regresses in the wild.
        try {
          const sc = cart || {};
          const sNvd = nvdItemsOf(sc);
          const sTarget = sc.items ? calculateVariant(sc.items, sc.currency) : null;
          console.log('[SP] checkout', {
            settled,
            spVariant: sNvd[0] && sNvd[0].variant_id,
            targetVariant: sTarget && sTarget.id,
            spLines: sNvd.length,
            spQty: sNvd.reduce((a, i) => a + i.quantity, 0), // total units — >1 means double-charge
          });
        } catch (err) { /* never block submit on a log */ }
        // Only divert to the empty-cart cleanup for a VALID cart that genuinely has no real items
        // (SP-only). Any malformed/failed read (no .items) falls straight through to submit — never
        // block checkout on a bad fetch.
        if (cart && Array.isArray(cart.items) && nonNvdItems(cart).length === 0) { enforceEmptyCart(cart); return; }
        submit();
      }
      proceed().catch(() => { checkoutInFlight = false; submit(); });
    }, true);
  }
  async function loadVariants() {
    const res = await fetch(ROOT + 'products/' + HANDLE + '.js');
    if (!res.ok) return false;
    const product = await res.json();
    variants = product.variants.map(v => ({
      id: v.id,
      price: v.price,                                          // market (presentment) cents — for display
      aud: Math.round(parseFloat(v.option1 || v.title) * 100), // static AUD ladder (title never converts) — for tier selection
    })).filter(v => v.aud > 0).sort((a, b) => a.aud - b.aud);
    return variants.length > 0;
  }
  // Registered unconditionally and synchronously here — before any async work below — so there is no
  // gap between page load and the checkout button being guarded. See guardCheckout()'s own comment.
  guardCheckout();
  // Hand off from the inline early-guard (navidium-scripts.liquid). Because THIS file is deferred, the
  // checkout button is clickable before guardCheckout() runs; the early-guard swallows any checkout
  // click in that window. Now that the real guard is live, tell it to stand down and replay a caught
  // click through the full verify-and-fix path. Without this, a spam-click at page load takes the
  // native checkout with a possibly stale/wrong-tier SP and no verify pass (no guard log at all).
  if (window.__spEarlyGuard && typeof window.__spEarlyGuard.activate === 'function') {
    try { window.__spEarlyGuard.activate(); } catch (e) { /* never block script init on the handoff */ }
  }
  const onCartUpdated = debounce((e) => {
    // init() hasn't finished (or there's nothing to sync on this page). Its opening syncProtection is
    // several round trips long — remove + add + cart + section — and this listener used to be
    // registered only AFTER that await, so a quantity click landing during it was dropped on the floor
    // with nothing left to re-trigger a sync. Reproduced exactly: Remove one product, watch SP swap to
    // the remaining product's tier (that swap IS init's first sync), click minus during it, and the
    // cart settles at the lower quantity still carrying the higher tier. Flag it instead — whichever
    // sync is running picks it up, and its finally-tail re-syncs if the loop has already moved on.
    if (!ready || !variants.length) { pending = false; resyncNeeded = true; return; }
    // Previously: `if (isUpdating) return;` — this silently threw the change away when a swap was
    // already running, which is how a fast second quantity click left a stale-tier SP behind. Hand
    // it to syncProtection instead; it coalesces (resyncNeeded) and re-verifies from a fresh read.
    if (isUpdating) { pending = false; resyncNeeded = true; return; }
    // `pending` stays TRUE across the drain+fetch below and is only cleared once syncProtection has
    // taken over the gate (it sets isUpdating). Clearing it here instead would open a window where
    // ready/isUpdating/pending are all false while the cart is known-changed but not yet re-synced —
    // a checkout click landing there would sail straight through on the old tier.
    // Show the event's cart immediately so the widget price doesn't visibly lag, but NEVER pick the
    // protection tier from it. e.detail.cart is whatever payload the dispatcher happened to hold,
    // and any un-serialized cart writer (a theme path we haven't chained, a third-party app, the
    // cart drawer) can hand us a pre-change cart — which is exactly how a stale tier got locked in.
    // The authoritative read below is our own no-store /cart.js, taken after writes have drained.
    const preview = e.detail && e.detail.cart;
    if (preview && Array.isArray(preview.items)) updateWidgetPrice(preview);
    waitForCartWrites()
      .then(fetchCartSettled)
      .then((c) => {
        lastCart = c;
        // NVD-only cart → clear it and show the empty cart instead of continuing
        if (nonNvdItems(c).length === 0 && nvdItemsOf(c).length > 0) { pending = false; enforceEmptyCart(c); return; }
        updateWidgetPrice(c);
        const p = syncProtection(c);
        pending = false; // syncProtection has synchronously claimed isUpdating — the gate is handed over
        return p;
      })
      .catch(() => { pending = false; });
  }, DEBOUNCE_MS);
  // Registered at module scope, synchronously alongside guardCheckout() — NOT at the end of init().
  // `pending` flips true on receipt, before the debounce delay runs the handler, closing the window
  // where a checkout click could land after the cart was mutated but before syncProtection has
  // started (and set isUpdating).
  document.addEventListener('cart:updated', (e) => {
    pending = true;
    onCartUpdated(e);
  });

  async function init() {
    // Every bail-out below leaves the page with no sync loop to consume a resyncNeeded flag the
    // module-scope cart:updated listener may have raised during setup. Left set, it would block
    // guardCheckout for the full CHECKOUT_WAIT_MS and then strip protection — so clear it on the way
    // out. Only the path that actually runs syncProtection keeps the flag (its tail re-syncs).
    const bail = () => { resyncNeeded = false; ready = true; };
    const hasWidget = !!document.querySelector('.nvd-mini');
    const onCartPage = !!document.querySelector('.page-width--cart');
    if (!hasWidget && !onCartPage) { bail(); return; }
    // Fetch the cart and load variants in parallel — they're independent, and on a cold page load
    // each round trip can take several hundred ms on real network latency, so doing them sequentially
    // was costing a full extra round trip out of the checkout guard's already-tight time budget.
    const variantsPromise = hasWidget ? loadVariants() : Promise.resolve(null);
    const cart = await fetchCart();
    lastCart = cart;
    // Clean up a protection-only cart even when the widget isn't rendered (empty-cart page now hides it)
    if (await enforceEmptyCart(cart)) { bail(); return; }
    if (!hasWidget) {
      // No widget on this page (variants never loaded) — still self-heal a stray duplicate a previous
      // page's interrupted swap may have left behind before allowing checkout through.
      const nvd = nvdItemsOf(cart);
      if (nvd.length > 1) {
        for (const item of nvd) await cartRemove(item.key);
      }
      bail();
      return;
    }
    if (!(await variantsPromise)) { bail(); return; }
    renderWidget(cart);
    await syncProtection(cart);
    ready = true;
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }
})();
